import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MoreVertical, ArrowLeft, Sparkles, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SimpleChatButton, SimpleChatStage } from '@/lib/api';
import FounderAvatar from '@/components/brand/FounderAvatar';
import SocialRow from '@/components/brand/SocialRow';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

interface SimpleChatWindowProps {
    messages: Message[];
    buttons: SimpleChatButton[];
    expectingText: boolean;
    inputPlaceholder: string;
    onButtonClick: (action: string) => void;
    onTextSubmit: (text: string) => void;
    onClose: () => void;
    onEndChat: () => void;
    isLoading: boolean;
    stage: SimpleChatStage;
}

const parseMarkdownLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        parts.push(
            <a
                key={keyIndex++}
                href={match[2]}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-secondary hover:text-secondary/80 underline underline-offset-[3px] decoration-secondary/40 hover:decoration-secondary transition-colors"
            >
                {match[1]}
            </a>
        );

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const STAGE_TITLE_KEY: Record<string, string> = {
    MAIN_MENU: 'main_menu',
    SELECT_PROJECT_TYPE: 'select_project_type',
    ADD_DETAILS_CONFIRM: 'add_details_confirm',
    SELECT_FEATURES: 'select_features',
    ENTER_PHONE: 'enter_phone',
    SELECT_BUDGET: 'select_budget',
    COMPLETED: 'completed',
    SERVICES_INFO: 'services_info',
    PORTFOLIO_INFO: 'portfolio_info',
    CONTACT_MANAGER: 'contact_manager',
    FREE_CHAT: 'free_chat',
    MEET_FOUNDER: 'meet_founder',
};

const MAX_INPUT_LEN = 500;

const SimpleChatWindow = ({
    messages,
    buttons,
    expectingText,
    inputPlaceholder,
    onButtonClick,
    onTextSubmit,
    onClose,
    onEndChat,
    isLoading,
    stage,
}: SimpleChatWindowProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [input, setInput] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, buttons]);

    useEffect(() => {
        if (expectingText) {
            inputRef.current?.focus();
        }
    }, [expectingText]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (input.trim() && !isLoading) {
            onTextSubmit(input.trim());
            setInput('');
        }
    };

    const regularButtons = buttons.filter(btn => !btn.action?.startsWith('back_'));
    const backButton = buttons.find(btn => btn.action?.startsWith('back_'));

    const isFeatureSelection = stage === 'SELECT_FEATURES';
    const featureButtons = isFeatureSelection
        ? regularButtons.filter(btn => btn.action?.startsWith('feature_'))
        : [];
    const actionButtons = isFeatureSelection
        ? regularButtons.filter(btn => !btn.action?.startsWith('feature_'))
        : regularButtons;

    const stageKey = STAGE_TITLE_KEY[stage] ?? 'default';
    const stageTitle = t(`chat.stages.${stageKey}`, { defaultValue: t('chat.stages.default') });

    const showCharCounter = input.length >= MAX_INPUT_LEN * 0.8;

    return (
        <div
            className={cn(
                'rounded-[28px] shadow-2xl shadow-black/60 overflow-hidden flex flex-col relative',
                'h-[min(600px,calc(100dvh-7.5rem))] sm:h-[min(600px,calc(100dvh-8rem))]',
                // Mobile: plain opaque card. No glass, no backdrop-filter, no
                // cascade fight with the `.glass-v2-strong` @media fallback in
                // index.css. Desktop: the glass gradient + backdrop blur.
                isMobile
                    ? 'bg-card border border-white/10'
                    : 'glass-v2-strong',
                // Belt-and-suspenders: stop any touch drag inside the chat
                // panel from chaining scroll to the page beneath it.
                'overscroll-contain',
            )}
        >
            {/* Top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-70 pointer-events-none z-10"
                style={{
                    background:
                        'linear-gradient(90deg, transparent, hsl(260 90% 65%), hsl(190 90% 50%), transparent)',
                }}
            />

            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 relative">
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-60"
                    style={{
                        background:
                            'radial-gradient(340px circle at 50% -20%, hsl(260 90% 65% / 0.22), transparent 70%)',
                    }}
                />
                <div className="relative flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <FounderAvatar size={44} showOnlineDot />
                        <div className="min-w-0 flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-v2-display text-lg leading-none">
                                    <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                                        TRBD
                                    </span>
                                </h3>
                                <span className="font-v2-label text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/80 inline-flex items-center gap-1">
                                    <span className="relative inline-flex w-1.5 h-1.5">
                                        <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-60" />
                                        <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
                                    </span>
                                    AI
                                </span>
                            </div>
                            <span className="font-v2-label text-[10px] text-white/60 leading-none truncate">
                                {stageTitle}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-0.5 shrink-0">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={t('chat.aria_more')}
                                    className="p-2 rounded-lg hover:bg-white/10 transition-colors text-muted-foreground hover:text-foreground min-w-[40px] min-h-[40px] flex items-center justify-center"
                                >
                                    <MoreVertical className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border">
                                <DropdownMenuItem
                                    onClick={onEndChat}
                                    className="text-destructive focus:text-destructive cursor-pointer"
                                >
                                    {t('chat.end_chat')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t('chat.aria_close')}
                            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-muted-foreground hover:text-foreground min-w-[40px] min-h-[40px] flex items-center justify-center"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Messages — anchored to bottom so a single welcome message sits
                next to the buttons instead of floating at the top with a big
                empty gap. `min-h-full + justify-end` on the inner flex column
                is the classic pattern that survives overflow correctly.
                `min-h-0` is required: without it the implicit `min-height:auto`
                on flex children expands this past its flex-1 share, and
                `overflow-y-auto` stops scrolling on mobile.
                `overscroll-contain` stops a finger-drag from chaining into
                the page when the messages area has little/no content. */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 custom-scrollbar relative">
                <div className="flex flex-col justify-end min-h-full gap-3">
                {messages.map((message, idx) => {
                    const isUser = message.role === 'user';
                    return (
                        <motion.div
                            key={message.id}
                            initial={isMobile ? false : { opacity: 0, y: 8 }}
                            animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                            transition={isMobile ? undefined : { duration: 0.3, delay: Math.min(idx * 0.04, 0.2), ease: [0.22, 1, 0.36, 1] }}
                            className={cn('flex', isUser ? 'justify-start' : 'justify-end')}
                        >
                            <div
                                className={cn(
                                    'relative max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap',
                                    isUser
                                        ? 'rounded-2xl rounded-tr-md bg-gradient-to-br from-primary/25 to-primary/10 text-foreground border border-primary/25'
                                        : 'rounded-2xl rounded-tl-md bg-white/[0.04] text-foreground border border-white/10',
                                )}
                            >
                                {/* Assistant bubble: subtle top-left accent line */}
                                {!isUser && (
                                    <span
                                        aria-hidden
                                        className="absolute top-0 start-0 w-6 h-px rounded-full"
                                        style={{
                                            background:
                                                'linear-gradient(90deg, hsl(260 90% 65%), transparent)',
                                        }}
                                    />
                                )}
                                {parseMarkdownLinks(message.content)}
                            </div>
                        </motion.div>
                    );
                })}

                {isLoading && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="flex justify-end"
                    >
                        <div className="relative px-4 py-3 rounded-2xl rounded-tl-md bg-white/[0.05] border border-white/10 overflow-hidden">
                            {/* Shimmer sweep — desktop only. On mobile the
                                infinite `x` loop stays on the main thread and
                                burns battery for no visual payoff (mobile
                                backdrop-filter is disabled anyway). */}
                            {!isMobile && (
                                <motion.span
                                    aria-hidden
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            'linear-gradient(90deg, transparent 0%, hsl(260 90% 65% / 0.12) 50%, transparent 100%)',
                                    }}
                                    animate={{ x: ['-100%', '100%'] }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
                                />
                            )}
                            <div className="relative flex items-center gap-2">
                                <Sparkles className="w-3.5 h-3.5 text-primary/80" />
                                <div className="flex items-center gap-1">
                                    {[0, 1, 2].map((i) => (
                                        <motion.span
                                            key={i}
                                            className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary to-secondary"
                                            animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                                            transition={{
                                                duration: 0.9,
                                                repeat: Infinity,
                                                delay: i * 0.15,
                                                ease: 'easeInOut',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Buttons */}
            <AnimatePresence mode="wait">
                {!isLoading && buttons.length > 0 && (
                    <motion.div
                        key={`${stage}-buttons`}
                        initial={isMobile ? false : { opacity: 0, y: 8 }}
                        animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                        exit={isMobile ? undefined : { opacity: 0, y: -4 }}
                        transition={isMobile ? undefined : { duration: 0.22 }}
                        className="px-3 pb-2 max-h-[40vh] sm:max-h-[400px] overflow-y-auto overscroll-contain custom-scrollbar space-y-2"
                    >
                        {isFeatureSelection && featureButtons.length > 0 && (
                            <div className="grid grid-cols-2 gap-2">
                                {featureButtons.map((btn, i) => (
                                    <motion.button
                                        key={btn.action}
                                        initial={isMobile ? false : { opacity: 0, y: 6 }}
                                        animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                                        transition={isMobile ? undefined : { duration: 0.25, delay: i * 0.03 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => onButtonClick(btn.action)}
                                        className={cn(
                                            'relative px-2.5 py-2 rounded-xl text-xs font-medium transition-all text-start min-h-[40px] flex items-center gap-2',
                                            btn.selected
                                                ? 'bg-gradient-to-br from-primary/30 to-secondary/20 border-2 border-primary/60 text-white shadow-lg shadow-primary/25'
                                                : 'bg-white/5 border border-white/10 text-foreground hover:bg-white/10 hover:border-white/20',
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'shrink-0 w-4 h-4 rounded-md flex items-center justify-center transition-all',
                                                btn.selected
                                                    ? 'bg-gradient-to-br from-primary to-secondary'
                                                    : 'bg-white/5 border border-white/15',
                                            )}
                                        >
                                            {btn.selected && <Check className="w-3 h-3 text-white" />}
                                        </span>
                                        <span className="flex-1 leading-tight">
                                            {btn.text.replace(/^✅\s*/, '')}
                                        </span>
                                    </motion.button>
                                ))}
                            </div>
                        )}

                        <div className={cn('grid gap-2', actionButtons.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
                            {actionButtons.map((btn, i) => {
                                const isPrimary = actionButtons.length === 1;
                                const cls = cn(
                                    'relative overflow-hidden px-3 py-2 rounded-xl text-xs font-medium border transition-all flex items-center justify-center text-center h-full min-h-[40px]',
                                    isPrimary
                                        ? 'bg-gradient-to-r from-primary to-secondary text-white border-transparent shadow-lg shadow-primary/30 hover:shadow-primary/50'
                                        : 'bg-white/[0.04] border-white/10 text-foreground hover:bg-white/[0.08] hover:border-primary/40',
                                );
                                const content = (
                                    <>
                                        {/* Primary-button shimmer — desktop only. Infinite `x`
                                            loops on mobile are the biggest battery / jank culprit
                                            and the effect adds nothing on a phone. */}
                                        {isPrimary && !isMobile && (
                                            <motion.span
                                                aria-hidden
                                                className="absolute inset-0 pointer-events-none"
                                                style={{
                                                    background:
                                                        'linear-gradient(90deg, transparent, hsl(0 0% 100% / 0.18), transparent)',
                                                }}
                                                animate={{ x: ['-100%', '100%'] }}
                                                transition={{
                                                    duration: 2.2,
                                                    repeat: Infinity,
                                                    ease: 'linear',
                                                    repeatDelay: 1.5,
                                                }}
                                            />
                                        )}
                                        <span className="relative">{btn.text}</span>
                                    </>
                                );
                                const common = {
                                    initial: isMobile ? false : { opacity: 0, y: 6 },
                                    animate: isMobile ? undefined : { opacity: 1, y: 0 },
                                    transition: isMobile ? undefined : { duration: 0.25, delay: i * 0.04 },
                                    whileTap: { scale: 0.98 },
                                    className: cls,
                                };
                                if (btn.url) {
                                    return (
                                        <motion.a
                                            key={btn.url + btn.text}
                                            href={btn.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            {...common}
                                        >
                                            {content}
                                        </motion.a>
                                    );
                                }
                                return (
                                    <motion.button
                                        key={btn.action}
                                        type="button"
                                        onClick={() => onButtonClick(btn.action)}
                                        {...common}
                                    >
                                        {content}
                                    </motion.button>
                                );
                            })}
                        </div>

                        {backButton && (
                            <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.1 }}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                onClick={() => onButtonClick(backButton.action)}
                                aria-label={t('chat.aria_back')}
                                className="w-full px-4 py-2.5 rounded-xl text-xs font-v2-label text-white/60 hover:text-white bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-white/10 transition-all flex items-center justify-center gap-2 sticky bottom-0 backdrop-blur-md min-h-[40px]"
                            >
                                <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" />
                                {backButton.text}
                            </motion.button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Text input */}
            {expectingText && (
                <form onSubmit={handleSubmit} className="p-3 border-t border-white/10 relative">
                    <div
                        className={cn(
                            'flex items-center gap-2 rounded-xl p-1 transition-all border',
                            isInputFocused
                                ? 'border-primary/60 bg-white/[0.06] shadow-[0_0_0_3px_hsl(260_90%_65%_/_0.12)]'
                                : 'border-white/10 bg-white/[0.04]',
                        )}
                    >
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            placeholder={inputPlaceholder || t('chat.input_placeholder_default')}
                            disabled={isLoading}
                            className="flex-1 bg-transparent border-0 px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
                            dir="auto"
                            maxLength={MAX_INPUT_LEN}
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                            aria-label={t('chat.aria_send')}
                            className={cn(
                                'relative overflow-hidden p-2.5 rounded-lg text-white transition-all min-w-[40px] min-h-[40px] flex items-center justify-center',
                                'bg-gradient-to-br from-primary to-secondary',
                                'disabled:opacity-40 disabled:cursor-not-allowed',
                                'enabled:hover:shadow-lg enabled:hover:shadow-primary/40',
                                'enabled:active:scale-95',
                            )}
                        >
                            <Send className="w-4 h-4 rtl:-scale-x-100 relative" />
                        </button>
                    </div>
                    {showCharCounter && (
                        <div className="absolute end-4 -top-2">
                            <span
                                className={cn(
                                    'font-v2-label text-[9px] px-1.5 py-0.5 rounded-full border',
                                    input.length >= MAX_INPUT_LEN
                                        ? 'bg-destructive/20 border-destructive/40 text-destructive-foreground'
                                        : 'bg-white/5 border-white/10 text-white/60',
                                )}
                            >
                                {input.length}/{MAX_INPUT_LEN}
                            </span>
                        </div>
                    )}
                </form>
            )}

            {/* Persistent social follow strip */}
            <div
                className="px-4 py-2 border-t border-white/5 flex items-center justify-between bg-background/30 relative"
                style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
            >
                <span className="font-v2-label text-[9px] text-white/50">
                    {t('chat.follow_us')}
                </span>
                <SocialRow variant="icon" iconSize={14} />
            </div>
        </div>
    );
};

export default SimpleChatWindow;
